import React, { createContext, useState, useContext, useEffect } from 'react';
import soundManager, {
  playBackgroundMusic,
  pauseBackgroundMusic,
  stopBackgroundMusic,
  isInGamePage,
  playPressSoundDirectly,
} from '../utils/soundUtils';

type AudioContextType = {
  isMusicEnabled: boolean;
  isSoundEnabled: boolean;
  toggleMusic: () => void;
  toggleSound: () => void;
  isMusicButtonDisabled: boolean;
  isSoundButtonDisabled: boolean;
};

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMusicEnabled, setIsMusicEnabled] = useState(() => {
    const savedMusicState = localStorage.getItem('musicEnabled');
    return savedMusicState ? JSON.parse(savedMusicState) : true;
  });

  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    const savedSoundState = localStorage.getItem('soundEnabled');
    return savedSoundState ? JSON.parse(savedSoundState) : true;
  });

  const [isMusicButtonDisabled, setIsMusicButtonDisabled] = useState(false);
  const [isSoundButtonDisabled, setIsSoundButtonDisabled] = useState(false);

  useEffect(() => {
    localStorage.setItem('musicEnabled', JSON.stringify(isMusicEnabled));
    soundManager.setMusicEnabled(isMusicEnabled);
  }, [isMusicEnabled]);

  useEffect(() => {
    localStorage.setItem('soundEnabled', JSON.stringify(isSoundEnabled));
    soundManager.setSoundEffectsEnabled(isSoundEnabled);

    if (isSoundEnabled && !soundManager.isEnabled()) {
      soundManager.setEnabled(true);
    }
  }, [isSoundEnabled]);

  const toggleMusic = () => {
    if (isMusicButtonDisabled) return;

    setIsMusicButtonDisabled(true);

    const newMusicState = !isMusicEnabled;

    setIsMusicEnabled(newMusicState);

    if (newMusicState) {
      soundManager.setMusicEnabled(true);
    } else {
      pauseBackgroundMusic();
      setTimeout(() => {
        soundManager.setMusicEnabled(false);
      });
    }

    setTimeout(() => {
      setIsMusicButtonDisabled(false);
    }, 1500);
  };

  const toggleSound = () => {
    if (isSoundButtonDisabled) return;

    setIsSoundButtonDisabled(true);

    setIsSoundEnabled((prev) => !prev);

    setTimeout(() => {
      setIsSoundButtonDisabled(false);
    }, 1500);
  };

  return (
    <AudioContext.Provider
      value={{
        isMusicEnabled,
        isSoundEnabled,
        toggleMusic,
        toggleSound,
        isMusicButtonDisabled,
        isSoundButtonDisabled,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};
